import { Component, OnInit, AfterContentInit, ViewChild, ElementRef, OnDestroy } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { JanusService } from '@shared/services/janus.service';
import { StateService } from '@shared/services/state.service';
import { SettingsComponent } from '@shared/components/settings/settings.component';
import { SettingsService } from '@shared/services/settings.service';
import { PopupService } from '@shared/services/popup/popup.service';
import { MediaService } from '@shared/services/media.service';
import { SoundService } from '@shared/services/sound.service';
import { AnalyticsService } from '@shared/services/analytics.service';
import { CustomizationModel } from '../../../models/customization.model';
import { CustomizationService } from '../../../services/customization.service';
import { SettingsChanges } from '../../../models/settings-changes.model';
import { PlayLeaveSoundState } from '../../../enums/play-leave-sound-state';
import { BackgroundEffectsType } from '@enums';
import { VideoEffectsService } from '@shared/services/video-effects/video-effects.service';

@Component({
  selector: 'thevatra-auth',
  templateUrl: './auth.component.html',
  styleUrls: ['./auth.component.scss'],
})
export class AuthComponent implements OnInit, OnDestroy, AfterContentInit {
  destroyed$ = new Subject<boolean>();

  @ViewChild('videoPreview') protected videoPreview: ElementRef<HTMLVideoElement>;
  public microphoneAccess: string;
  public previewStream: MediaStream | null;

  public isSirius = false;
  public customConfig: CustomizationModel;
  public hasMediaPermission = true;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private janusService: JanusService,
    public stateService: StateService,
    private soundService: SoundService,
    private settingsService: SettingsService,
    private popupService: PopupService,
    private media: MediaService,
    public customizationService: CustomizationService,
    public videoEffectsService: VideoEffectsService,
  ) {
    this.stateService.isCamBanned = false;
    if (!this.hasPermissionPage()) {
      this.media.devicesGetUserMedia().subscribe(hasPermission => {
        this.hasMediaPermission = hasPermission;
        this.stateService.isCamBanned = !hasPermission;
      });
    } else {
      this.hasMediaPermission = false;
    }
    // check access to cam

    this.isSirius = customizationService.isSirius();
    this.customConfig = customizationService.config;
  }

  async ngOnInit(): Promise<any> {
    this.handleSettingsChanges();

    if (this.stateService.playLeaveSound === PlayLeaveSoundState.play) { // We need to use few values as we have reload on logout
      this.soundService.playLeave();
      this.stateService.playLeaveSound = PlayLeaveSoundState.default;
    } else if (this.stateService.playLeaveSound === PlayLeaveSoundState.reloadAndPlay) {
      this.stateService.playLeaveSound = PlayLeaveSoundState.play;
    }

    this.settingsService.getDevices().subscribe(({ audioDevices, videoDevices }) => {
      if (audioDevices?.length && !audioDevices[0].deviceId) {
        this.microphoneAccess = 'access_denied';
      }
    });
    if (!this.stateService.videoEnabled && this.stateService.isCamEnabled) {
      this.stateService.isCamEnabled = false; // turn of preview
    }
    this.stateService.isPreviewStreamActive$.subscribe(isPreviewStreamActive => {
      if (isPreviewStreamActive && this.previewStream) {
        this.media.saveConstraints(this.previewStream);
        this.stopVideoPreview();
      }
    });
  }

  onMediaPermissionChange(permission: boolean) {
    this.hasMediaPermission = permission;
  }

  ngAfterContentInit(): void {
    if (!this.hasPermissionPage()) {
      this.stateService.isCamEnabled$.pipe(takeUntil(this.destroyed$)).subscribe((isCamEnabled) => {
        if (isCamEnabled) {
          this.initVideoPreview(this.stateService.videoDeviceId);
        } else if (isCamEnabled === null) {
          this.stateService.isCamEnabled = true;
        } else { // @here: the place to check for no device enter
          this.stopVideoPreview();
          this.stateService.videoEnabled = false;
        }
      });
    }
  }

  hasPermissionPage(): boolean {
    return !!this.stateService.getSavedState('displayPermission', 'string');
  }

  public accessMicrophone(): void {
    // TODO: In Chrome we need to repload page after change permission

    // from(navigator.mediaDevices.getUserMedia({ audio: true }))
    //   .pipe(takeUntil(this.destroyed$))
    //   .subscribe(res => {
    //     this.microphoneAccess = '';
    //   }, err => {
    //     this.microphoneAccess = 'access_denied';
    //   });
  }

  protected async initVideoPreview(preview: string | null | MediaStream): Promise<void> {
    if (this.previewStream) {
      this.stopVideoPreview();
    }
    if (preview instanceof MediaStream) {
      this.previewStream = preview;
      this.videoPreview.nativeElement.srcObject = this.previewStream;
      this.media.saveConstraints(preview);
      this.microphoneAccess = '';
    } else {
      this.media.getMediaStream(this.stateService.constraints).then((stream: MediaStream) => {
        this.previewStream = stream;
        this.videoPreview.nativeElement.srcObject = this.previewStream;
        this.microphoneAccess = '';
        this.changeVideoPreview();
      }).catch((error) => {
        if (error.name === 'ConstraintNotSatisfiedError') {
          console.error('Current resolution is not supported by your device.');
        } else if (error.name === 'PermissionDeniedError') {
          console.error('Permissions have not been granted to use your camera and ' +
            'microphone, you need to allow the page access to your devices');
        } else if ('NotAllowedError' === error.name) { // User blocked cam
          // console.error(`[loginC] cam is banned `, error);
          // this.microphoneAccess = 'access_denied';
          this.stateService.isCamBanned = true;
        } else if ('NotFoundError' === error.name) { // user don't have cam
          this.stateService.isCamBanned = true;
        } else {
          console.error(`[loginC] getMediaStream error: ${error.name} :: `, error);
        }
      });
    }
  }

  public toggleMic(): void {
    AnalyticsService.addBreadcrumb('janus', 'Toggle mic in login.');
    this.stateService.isMicEnabled = !this.stateService.isMicEnabled;
    this.stateService.audioEnabled = this.stateService.isMicEnabled;
  }

  public toggleCam(): void {
    AnalyticsService.addBreadcrumb('janus', 'Toggle camera in login.');
    this.stateService.isCamEnabled = !this.stateService.isCamEnabled;
    this.stateService.videoEnabled = this.stateService.isCamEnabled;
  }

  public stopVideoPreview(): void {
    if (this.previewStream) {
      this.previewStream.getTracks().forEach((track) => {
        track.enabled = false;
        if (track.kind === 'video') {
          track.stop();
        }
      });
      this.previewStream = null;
    }
  }

  public openSettings(): void {
    AnalyticsService.addBreadcrumb('janus', 'Open settings in login.');
    this.popupService
      .dialog(
        SettingsComponent,
        { stream: this.previewStream, closeByEsc: false, panelClass: 'settings-dialog' },
        { top: '195px', isCentredHorizontally: true }
      )
      .afterClosed$.subscribe();
  }

  private handleSettingsChanges(): void {
    this.stateService.settingsChanges$
      .asObservable()
      .pipe(
        takeUntil(this.destroyed$),
        filter(value => !!value),
        map(value => value as SettingsChanges)
      )
      .subscribe((value: SettingsChanges) => {
        const videoDeviceId = value.videoDeviceId || undefined;
        const audioDeviceId = value.audioDeviceId || undefined;

        this.media.saveConstraints(null, videoDeviceId, audioDeviceId);
        this.initVideoPreview(null);
      });
  }

  private changeVideoPreview(): Promise<void> {
    if (this.stateService.currentBackgroundEffect === BackgroundEffectsType.none) {
      this.videoEffectsService.stopRedrawingCanvas();
      return Promise.resolve();
    }

    this.videoEffectsService.stopRedrawingCanvas();
    this.videoEffectsService.sendVideoChangesForRedrawing(this.previewStream).subscribe();

    return Promise.resolve();
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
